import { createTheme } from "@mui/material/styles";

/**
 * Single source of truth for color + typography.
 *
 * Direction: PayLoom brand identity — Archivo for display, Work Sans for text,
 * plum as the default action, marigold reserved for the one thing on screen
 * that matters most, on a warm blush page.
 *
 * Values are taken verbatim from the PayLoom Brand Identity book (Tokens
 * section) rather than eyedropped, which is the difference between one plum
 * and nine.
 *
 * Raw token values are also re-exported from `common/color.js` for the
 * components that import tokens directly.
 */

export const tokens = {
  // brand
  primary: "#8C3B5E", // plum — the default action
  primaryHover: "#5E2540",
  primaryTint: "#F1E4E6", // plum at 12% over card, flat rather than an alpha wash
  onPrimary: "#FFFCF8",

  /**
   * `accent` is this codebase's name for the interactive colour — active nav,
   * selected rows, links, checked boxes, emphasis figures. That is plum, not
   * marigold: the book reserves marigold for the single most important thing
   * on a screen, so it must never be reachable as a default.
   */
  accent: "#8C3B5E",
  accentHover: "#5E2540",
  accentTint: "#F1E4E6",

  /**
   * Marigold. Opt in deliberately — one per screen, and never for a state the
   * user did not ask for. Also available as MUI `color="secondary"`.
   */
  marigold: "#E8A93D",
  marigoldHover: "#D2941F",
  marigoldTint: "#FBEEDA",
  marigoldText: "#886424", // marigold darkened until it clears 4.5:1 on card and page
  onMarigold: "#4A2E10",

  // Retained so existing "text on marigold" call sites keep working.
  accentText: "#886424",
  onAccent: "#4A2E10",

  // status — solid for icons and borders, tint for backgrounds, text for labels on tint
  success: "#2E7D5B",
  successTint: "#E6F2EC",
  successText: "#286E51",
  warning: "#C97A16",
  warningTint: "#FBEEDA",
  warningText: "#91580E",
  danger: "#B3372F",
  dangerTint: "#FBE9E7",
  // The book's destructive-button border. Error needs no separate text value:
  // the solid already clears AA on card and blush alike.
  dangerBorder: "#E3B5B1",
  info: "#3E6E8E",
  infoTint: "#E8F0F5",
  infoText: "#3C6A88",
  pending: "#7A6A70",
  pendingTint: "#F0EBEC",
  pendingText: "#6C5E64",

  // neutrals
  text: "#2A1D22", // ink

  /**
   * Supporting text is plum, not grey. The book's Surface set is bg, card,
   * border, border-strong, ink and muted-dark — there is deliberately no
   * neutral grey for light surfaces, and plum on blush measures 6.55:1 (AA for
   * body text at 14px and up), 7.07:1 on card. Grey secondary text is what
   * makes the page read as warm-grey rather than plum-toned.
   */
  textMuted: "#8C3B5E",

  // Genuinely inert text, which is the one place a warm grey belongs:
  // placeholders in an empty field, and disabled controls.
  textPlaceholder: "#746B64",
  textDisabled: "#9A8E84",
  // From the book's Navigation section: a disabled tab or pagination control
  // (#A39890), and the faint separator between breadcrumb levels (#C4B9B2).
  textInactive: "#A39890",
  textFaint: "#C4B9B2",
  border: "#EAD8CC",
  borderStrong: "#DCC5B3",
  surface: "#FBF2EC", // blush page
  surfaceSubtle: "#F5E9E1",
  card: "#FFFCF8",
  mutedDark: "#E4CDD5", // blush, for reversed surfaces
  /**
   * There is deliberately no pure-white token.
   *
   * The book's Surface set is bg, card, border, border-strong, ink and
   * muted-dark — #FFFFFF is not among them, and the card ground is the warm
   * #FFFCF8. A `white` token existed and had reached 78 surfaces, each one a
   * shade colder than the panel beside it. Use `card` for a surface and
   * `onPrimary` for text sitting on a solid fill.
   */

  /**
   * The app sidebar is a dark plum panel, per the book's "In product" spread:
   * a plum-dark ground, the reversed lockup, the active row filled with solid
   * plum, and every other row in blush. It is the one large dark surface in
   * the product, which is what keeps the blush page beside it feeling light.
   */
  sidebarBg: "#5E2540",
  // Hover on the rail is a darkening of its own ground, never a lighter tint —
  // a pale wash there reads as a second selected row competing with the real one.
  sidebarHoverBg: "#491C31",
  sidebarActiveBg: "#8C3B5E",
  sidebarText: "#E4CDD5",
  sidebarTextActive: "#FBF2EC",

  /**
   * Inline messages carry their own text colours, darker than the badge text
   * variants because a message is a run of prose rather than a one-word label.
   * The dot uses the standard text variant. All four measure 6.5-7.1:1 on
   * their tint. Values are the book's, from Overlays and feedback.
   */
  msgSuccessText: "#1F5940",
  msgWarningText: "#7A4A0B",
  msgDangerText: "#8F2C25",
  msgInfoText: "#2F5570",

  // dark surfaces
  darkBg: "#1B1216",
  darkSurface: "#241A1F",
  darkRaised: "#2F2229",
  darkBorder: "#453540",
  darkText: "#F7EFF2",
  darkMuted: "#C9B3BC",
  darkPrimary: "#E39BB8",
};

/** Categorical chart series, in order. Never reorder — series keep their colour. */
export const chartColors = [
  "#8C3B5E",
  "#E8A93D",
  "#3E6E8E",
  "#2E7D5B",
  "#C4674A",
  "#7A5B8E",
];

/**
 * The type scale: ten steps, no improvising between them.
 *
 * The root font size is a fixed 16px (see index.css), so each `size` below is
 * exactly the pixel value the book names for it. Archivo covers display
 * through H3; Work Sans covers everything a user reads or types into.
 *
 * Minimum body size in product is 14px. 13px is permitted only for caption and
 * label, which are helper text and small caps — never running prose.
 */
export const type = {
  display:   { size: "3rem",      lh: 1.1,  weight: 800, font: "display" }, // 48 — marketing only
  h1:        { size: "2.125rem",  lh: 1.15, weight: 700, font: "display" }, // 34 — page title
  h2:        { size: "1.75rem",   lh: 1.2,  weight: 700, font: "display" }, // 28 — section
  h3:        { size: "1.25rem",   lh: 1.3,  weight: 600, font: "display" }, // 20 — card title
  bodyLarge: { size: "1rem",      lh: 1.7,  weight: 400, font: "text" },    // 16 — intros
  body:      { size: "0.9375rem", lh: 1.6,  weight: 400, font: "text" },    // 15 — default
  small:     { size: "0.875rem",  lh: 1.6,  weight: 400, font: "text" },    // 14 — table cells
  caption:   { size: "0.8125rem", lh: 1.5,  weight: 500, font: "text" },    // 13 — helper text
  label:     { size: "0.8125rem", lh: 1.5,  weight: 600, font: "text",      // 13 — 0.06em caps
               tracking: "0.06em", transform: "uppercase" },
};

/** The eight legal font sizes, smallest first. Nothing sits between them. */
export const typeSizes = [
  "0.8125rem", // 13
  "0.875rem",  // 14
  "0.9375rem", // 15
  "1rem",      // 16
  "1.25rem",   // 20
  "1.75rem",   // 28
  "2.125rem",  // 34
  "3rem",      // 48
];

/** A 4px base. Every gap, pad and inset is one of these eleven values. */
export const space = [4, 8, 12, 16, 20, 24, 32, 40, 48, 60, 80];

export const radius = {
  sm: 6, // inline chips, small tags
  md: 8, // buttons, inputs, selects
  /**
   * The frame draws a hard line between the two kinds of box.
   *
   * `card` (14) is a band on the page — the thing that sits directly on the
   * blush with a 32px pad. `panel` (10) is only ever a box *inside* a card: a
   * table shell, a breakdown row, a summary block. Nothing else is legal, and
   * the difference is what tells a reader which boxes are peers and which are
   * contents.
   */
  card: 14,
  panel: 10,
  inner: 10, // alias of `panel`, kept for existing call sites
  lg: 12, // dialogs and menus only — a dialog is not a card on the page
  pill: 999, // status pills, avatars
};

/**
 * The frame. Nothing in the book changes these three numbers — a 64px bar
 * across the top, a 240px rail down the left, and one scrolling column between
 * them — so they live here once and the shell components read them.
 *
 * Every value is a plain number of CSS pixels. `px()` is provided so a call
 * site never has to remember to append the unit.
 */
export const frame = {
  topBarHeight: 64,
  sidebarWidth: 240,
  sidebarCollapsedWidth: 72,
  /** The gutter each side of the content column, and its narrow value. */
  gutter: 32,
  gutterNarrow: 20,
  /** Fluid between these two, then centred. */
  contentMaxWidth: 1240,
  /** Below this the rail is an icon rail and the narrow values apply. */
  compactBreakpoint: 1024,
  /** Below this the phone app takes over; out of scope for the web product. */
  phoneBreakpoint: 768,
  /** Between bands, and between cards sitting side by side within a band. */
  cardGap: 20,
  cardPadding: 32,
  cardPaddingNarrow: 24,
  /** The page header once it has stuck to the top of the content column. */
  stickyHeaderHeight: 52,
};

/** `frame` value as a CSS length. `px("gutter") === "32px"`. */
export const px = (key) => `${frame[key]}px`;

/** Warm shadows, tinted with ink rather than pure black. Never stack two. */
export const elevation = {
  flat: "none", // default — cards use a 1px border instead of a shadow
  raised: "0 4px 12px -4px rgba(42,29,34,0.16)", // dropdowns, popovers, date pickers
  floating: "0 16px 40px -20px rgba(42,29,34,0.22)", // dialogs, side sheets
  lifted: "0 20px 50px -20px rgba(42,29,34,0.26)", // marketing only, never in product
};

/** Nothing bounces, nothing overshoots. */
export const motion = {
  duration: {
    1: 120, // hover, focus, checkbox and toggle states
    2: 180, // dropdowns, tooltips, inline expand
    3: 240, // dialogs, side sheets, tab changes
    4: 320, // page and route transitions, the longest allowed
  },
  easing: {
    standard: "cubic-bezier(0.2, 0, 0.2, 1)",
    decelerate: "cubic-bezier(0, 0, 0.2, 1)", // something arriving
    accelerate: "cubic-bezier(0.4, 0, 1, 1)", // something leaving for good
    linear: "linear", // progress bars and spinners only
  },
};

/**
 * Tags and IDs are monospace, always — the module-screens book states it as a
 * flat rule. An asset tag, an employee number and an error reference are all
 * things people read character by character, compare against another screen, or
 * quote down a phone, and a proportional face makes 0/O and 1/l ambiguous
 * exactly when it matters.
 */
export const monoFamily = [
  "ui-monospace",
  "SFMono-Regular",
  "Menlo",
  "Consolas",
  '"Liberation Mono"',
  "monospace",
].join(",");

const displayFont = [
  "Archivo",
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "Helvetica",
  "Arial",
  "sans-serif",
].join(",");

const fontFamily = [
  "Work Sans",
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "Roboto",
  "Helvetica",
  "Arial",
  "sans-serif",
].join(",");

export { displayFont, fontFamily };

/** The focus ring: one ring, used everywhere. Keyboard focus only. */
const focusRing = {
  outline: `2px solid ${tokens.primary}`,
  outlineOffset: "2px",
};

const theme = createTheme({
  palette: {
    primary: {
      main: tokens.primary,
      dark: tokens.primaryHover,
      contrastText: tokens.card,
    },
    // Marigold is opt-in only: `color="secondary"`, one per screen.
    secondary: {
      main: tokens.marigold,
      dark: tokens.marigoldHover,
      contrastText: tokens.onMarigold,
    },
    success: { main: tokens.success, contrastText: tokens.onPrimary },
    warning: { main: tokens.warning, contrastText: tokens.onPrimary },
    error: { main: tokens.danger, contrastText: tokens.onPrimary },
    info: { main: tokens.info, contrastText: tokens.onPrimary },
    text: {
      primary: tokens.text,
      secondary: tokens.textMuted,
    },
    divider: tokens.border,
    background: {
      default: tokens.surface,
      paper: tokens.card,
    },
  },
  /**
   * MUI animates Dialog, Popover, Menu, Tooltip, Collapse, Drawer, Fade and the
   * Tabs indicator off `theme.transitions`, not off any `sx` we write. Left at
   * its defaults it runs 225ms in / 195ms out on `cubic-bezier(0.4, 0, 0.2, 1)`
   * — none of which is on the book's scale, so a dialog opened in 225ms while
   * every hand-written transition around it ran at 240ms.
   *
   * MUI's own `easeOut` and `easeIn` are already byte-identical to the book's
   * decelerate and accelerate, so only `standard` actually changes shape.
   */
  transitions: {
    duration: {
      shortest: motion.duration[1], // hover, focus, checkbox and toggle
      shorter: motion.duration[2],
      short: motion.duration[2], // dropdowns, tooltips, inline expand
      standard: motion.duration[3], // dialogs, side sheets, tab changes
      complex: motion.duration[4],
      enteringScreen: motion.duration[3],
      leavingScreen: motion.duration[3],
    },
    easing: {
      easeInOut: motion.easing.standard, // "almost everything, enters and exits alike"
      easeOut: motion.easing.decelerate, // something arriving
      easeIn: motion.easing.accelerate, // something leaving for good
      sharp: motion.easing.standard,
    },
  },
  shape: { borderRadius: radius.md },
  shadows: [
    "none",
    elevation.raised,
    elevation.raised,
    elevation.raised,
    elevation.floating,
    elevation.floating,
    elevation.floating,
    elevation.floating,
    elevation.lifted,
    ...Array(16).fill(elevation.lifted),
  ],
  /**
   * Ten steps, no improvising between them. Archivo covers display through H3,
   * Work Sans covers everything a user reads or types into. Minimum body size
   * in product is 14px.
   */
  typography: {
    fontFamily,
    // Display — marketing only
    h1: {
      fontFamily: displayFont,
      fontWeight: 800,
      fontSize: "3rem",
      lineHeight: 1.1,
      letterSpacing: "-0.02em",
    },
    // Page title
    h2: {
      fontFamily: displayFont,
      fontWeight: 700,
      fontSize: "2.125rem",
      lineHeight: 1.15,
      letterSpacing: "-0.02em",
    },
    // Section
    h3: {
      fontFamily: displayFont,
      fontWeight: 700,
      fontSize: "1.75rem",
      lineHeight: 1.2,
      letterSpacing: "-0.015em",
    },
    // Card title
    h4: {
      fontFamily: displayFont,
      fontWeight: 600,
      fontSize: "1.25rem",
      lineHeight: 1.3,
    },
    // h5/h6 have no counterpart in the book, which stops at H3. They exist only
    // to fill MUI's remaining heading slots; 1.4 is interpolated between H3's
    // 1.3 and body's 1.6, since a display-font heading at this size reads as
    // loose at the body ratio. Prefer h3 + caption over reaching for these.
    h5: { fontFamily: displayFont, fontWeight: 600, fontSize: "1rem", lineHeight: 1.4 },
    h6: { fontFamily: displayFont, fontWeight: 600, fontSize: "0.9375rem", lineHeight: 1.4 },
    // Body large — intros
    subtitle1: { fontWeight: 400, fontSize: "1rem", lineHeight: 1.7 },
    subtitle2: { fontWeight: 600, fontSize: "0.9375rem", lineHeight: 1.6 },
    // Body — default
    body1: { fontWeight: 400, fontSize: "0.9375rem", lineHeight: 1.6 },
    // Small — table cells
    body2: { fontWeight: 400, fontSize: "0.875rem", lineHeight: 1.6 },
    // Caption — helper text
    caption: { fontWeight: 500, fontSize: "0.8125rem", lineHeight: 1.5 },
    // Label
    overline: {
      fontWeight: 600,
      fontSize: "0.8125rem",
      lineHeight: 1.5,
      letterSpacing: "0.06em",
      textTransform: "uppercase",
    },
    button: { fontWeight: 600, fontSize: "0.875rem", textTransform: "none" },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily,
          backgroundColor: tokens.surface,
          color: tokens.text,
        },
        // Every currency figure, employee count and date uses tabular figures
        // so columns align.
        "th, td, .pl-numeric": { fontVariantNumeric: "tabular-nums" },
        // Honour reduced motion by cutting to the end state, not by shortening.
        "@media (prefers-reduced-motion: reduce)": {
          "*, *::before, *::after": {
            animationDuration: "0.01ms !important",
            animationIterationCount: "1 !important",
            transitionDuration: "0.01ms !important",
          },
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true, disableRipple: false },
      styleOverrides: {
        root: {
          textTransform: "none",
          borderRadius: radius.md,
          minHeight: 42,
          padding: "11px 20px",
          fontWeight: 600,
          transition: `background-color ${motion.duration[1]}ms ${motion.easing.standard}, border-color ${motion.duration[1]}ms ${motion.easing.standard}`,
          "&.Mui-focusVisible": focusRing,
          "&.Mui-disabled": {
            backgroundColor: tokens.border,
            color: tokens.textDisabled,
          },
        },
        outlined: {
          borderColor: tokens.borderStrong,
          color: tokens.primary,
          "&:hover": {
            borderColor: tokens.primary,
            backgroundColor: "transparent",
          },
        },
        // Destructive actions are never the visual default.
        outlinedError: { borderColor: "#E3B5B1", color: tokens.danger },
        text: {
          textDecoration: "underline",
          textUnderlineOffset: "3px",
          paddingLeft: 4,
          paddingRight: 4,
          "&:hover": { backgroundColor: "transparent" },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { "&.Mui-focusVisible": focusRing },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: tokens.primary,
          textUnderlineOffset: "3px",
          "&:hover": { color: tokens.primaryHover },
          "&.Mui-focusVisible": focusRing,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          // A field at rest sits on the page's blush and only lifts to card
          // once it has focus, so the one you are typing in is the one that
          // looks lit.
          backgroundColor: tokens.surface,
          "& .MuiOutlinedInput-notchedOutline": { borderColor: tokens.borderStrong },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: tokens.primary },
          // Focused state uses a 2px plum border.
          "&.Mui-focused": { backgroundColor: tokens.card },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: 2,
            borderColor: tokens.primary,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderWidth: 2,
            borderColor: tokens.danger,
          },
          "&.Mui-disabled": { backgroundColor: tokens.surface },
        },
        input: {
          fontSize: "0.875rem",
          padding: "11px 14px",
          "&::placeholder": { color: tokens.textPlaceholder, opacity: 1 },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: { fontSize: "0.8125rem", fontWeight: 600, color: tokens.text },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: { fontSize: "0.8125rem", marginLeft: 0 },  // caption
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radius.pill,
          height: "auto",
          padding: "5px 4px",
          fontSize: "0.8125rem",
          fontWeight: 600,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        // Cards use a 1px border instead of a shadow.
        rounded: { borderRadius: radius.lg },
        elevation0: { border: `1px solid ${tokens.border}` },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        // A MUI Card is a band on the page, so it takes the frame's card
        // radius, not the dialog radius it used to share.
        root: {
          borderRadius: radius.card,
          border: `1px solid ${tokens.border}`,
          backgroundColor: tokens.card,
        },
      },
    },
    /**
     * A modal means a decision cannot be deferred; everything else happens in
     * place. Max width 480, radius 12, 28px padding, and a plum scrim rather
     * than black — black over blush reads as a dead screen.
     */
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radius.lg,
          boxShadow: elevation.floating,
          maxWidth: 480,
          padding: 28,
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: { backgroundColor: "rgba(94, 37, 64, 0.32)" },
      },
    },
    // The dialog's own padding sits on the paper, so its parts do not re-pad.
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: displayFont,
          fontSize: "1.25rem",
          fontWeight: 600,
          lineHeight: 1.3,
          padding: 0,
          paddingBottom: 12,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: { root: { padding: 0 } },
    },
    MuiDialogActions: {
      styleOverrides: {
        // Confirm sits right and last.
        root: { padding: 0, paddingTop: 24, gap: 12 },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: radius.md,
          border: `1px solid ${tokens.border}`,
          boxShadow: elevation.raised,
        },
      },
    },
    /**
     * A focused row insets its ring so it does not overlap the rows either
     * side of it, and takes the page background to lift off the card. It lives
     * here rather than in a stylesheet because a linked row renders as an
     * anchor, not a tr, and MUI's own hover background would otherwise win.
     */
    MuiTableRow: {
      styleOverrides: {
        root: {
          "&:focus-visible": {
            outline: `2px solid ${tokens.primary}`,
            outlineOffset: "-2px",
            backgroundColor: tokens.surface,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor: tokens.border,
          fontSize: "0.875rem",
          fontVariantNumeric: "tabular-nums",
          // Row rhythm from the book's table. 18px is a component-internal
          // value like the button's 20px, not a step on the layout scale.
          padding: "14px 18px",
        },
        // Header row sits on the page background, not the card: plum, small
        // caps, letterspaced — a label, not a heading. It sits tighter than a
        // body row so the first row of data reads as the start of the list.
        head: {
          backgroundColor: tokens.surface,
          fontWeight: 600,
          fontSize: "0.8125rem",  // label
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          color: tokens.primary,
          padding: "12px 18px",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          "&.Mui-focusVisible": focusRing,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: tokens.text,
          fontSize: "0.8125rem",
          borderRadius: radius.sm,
          padding: "8px 12px",
        },
      },
    },
  },
});

export default theme;
